// FilterSort.tsx
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import {
  PiFunnel,
  PiSortAscending,
  PiSortDescending,
  PiTextAa,
} from 'react-icons/pi';

import { FilterSortType, FilterSortConfig } from './types';
import { SortButton } from '../../components/SortButton';

interface FilterSortProps {
  sortConfig: FilterSortConfig;
  setSortConfig: React.Dispatch<React.SetStateAction<FilterSortConfig>>;
}

const getSortIcon = (type: FilterSortType) => {
  switch (type) {
    case 'name':
      return PiTextAa;
    case 'type':
      return PiFunnel;
    default:
      return PiSortAscending;
  }
};

const getSortLabel = (type: FilterSortType) => {
  switch (type) {
    case 'name':
      return 'Name';
    case 'type':
      return 'Type';
    default:
      return 'Sort';
  }
};

export const FilterSort = ({ sortConfig, setSortConfig }: FilterSortProps) => {
  const handleSortChange = (
    type: FilterSortType,
    direction?: 'asc' | 'desc'
  ) => {
    setSortConfig({
      type,
      direction:
        direction ||
        (type === sortConfig.type && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc'),
    });
  };

  const toggleDirection = () => {
    setSortConfig((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SortButton
          direction={sortConfig.direction}
          label={getSortLabel(sortConfig.type)}
          hideLabel={true}
          asChild={true}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
          Sort by
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => handleSortChange('name')}
          className={sortConfig.type === 'name' ? 'bg-accent' : ''}
        >
          <PiTextAa className="mr-2 size-4" />
          Name
          {sortConfig.type === 'name' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleSortChange('type')}
          className={sortConfig.type === 'type' ? 'bg-accent' : ''}
        >
          <PiFunnel className="mr-2 size-4" />
          Type
          {sortConfig.type === 'type' && (
            <div className="ml-auto">
              {sortConfig.direction === 'asc' ? (
                <PiSortAscending className="size-4" />
              ) : (
                <PiSortDescending className="size-4" />
              )}
            </div>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleDirection}>
          <div className="flex items-center">
            {sortConfig.direction === 'asc' ? (
              <>
                <PiSortDescending className="mr-2 size-4" />
                Switch to descending
              </>
            ) : (
              <>
                <PiSortAscending className="mr-2 size-4" />
                Switch to ascending
              </>
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
